import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiService, Team } from '../services/api';

const Teams: React.FC = () => {
  const { year, group } = useParams<{ year: string; group: string }>();
  const [teams, setTeams] = useState<Team[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fallback logo handling (same pattern as Schedule/TeamDetail)
  const [failedLogos, setFailedLogos] = useState<Set<string>>(new Set());
  const getTeamLogoUrl = (teamId: string): string => {
    return `https://pub-e0ccb659989a49a297dc22911f822b08.r2.dev/photos/teams/logos/${teamId}.png`;
  };
  const handleLogoError = (teamId: string) => {
    setFailedLogos(prev => new Set(Array.from(prev).concat(teamId)));
  };
  const logoFailed = (teamId: string): boolean => failedLogos.has(teamId);

  useEffect(() => {
    const fetchData = async () => {
      if (!year || !group) return;
      
      setIsLoading(true);
      setError(null);
      
      try {
        const teamsData = await apiService.getTeams();
        const filteredTeams = teamsData.filter(
          team => team.year === parseInt(year) && team.group === group
        );
        setTeams(filteredTeams);
      } catch (err) {
        setError('Failed to load data');
        console.error('Error fetching data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [year, group]);

  if (isLoading) return <div style={{ textAlign: 'center', padding: '20px' }}>Loading...</div>;
  if (error) return <div style={{ textAlign: 'center', padding: '20px', color: 'red' }}>{error}</div>;
  if (!year || !group) return <div style={{ textAlign: 'center', padding: '20px' }}>Invalid URL parameters</div>;

  return (
    <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '20px', backgroundColor: '#e3f2fd', minHeight: '100vh' }}>
      {/* Navigation */}
      <div style={{ marginBottom: '30px' }}>
        <Link 
          to={`/summer-league/${year}/${group}`}
          style={{ 
            color: '#0066cc', 
            textDecoration: 'none', 
            fontSize: '16px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          ← 返回
        </Link>
      </div>
      
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '30px' }}>
        <h1 style={{ 
          fontSize: '2.5rem', 
          color: '#2d3748',
          marginBottom: '8px'
        }}>
          球隊介紹
        </h1>
        <h2 style={{ 
          fontSize: '1.8rem', 
          color: '#4a5568',
          marginBottom: '8px'
        }}>
          Summer League {year} - Group {group}
        </h2>
      </div>

      {/* Teams Grid */}
      <div>
        {teams.length > 0 ? (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
            gap: '25px',
            padding: '20px'
          }}>
            {teams.map((team) => (
              <Link 
                key={team.id} 
                to={`/summer-league/${year}/${group}/teams/${team.id}`}
                style={{
                  textDecoration: 'none',
                  color: 'inherit',
                  display: 'block'
                }}
              >
                <div style={{
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  padding: '25px',
                  backgroundColor: '#e3f2fd',
                  textAlign: 'center',
                  cursor: 'pointer',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  {/* Team Logo with fallback to computed URL and graceful 404 handling */}
                  {!logoFailed(team.id) ? (
                    <img
                      src={team.logo || getTeamLogoUrl(team.id)}
                      alt={`${team.name} Logo`}
                      style={{
                        width: '120px',
                        height: '120px',
                        objectFit: 'contain',
                        margin: '0 auto 20px',
                        backgroundColor: '#e3f2fd'
                      }}
                      onError={() => handleLogoError(team.id)}
                    />
                  ) : (
                    <div style={{
                      width: '120px',
                      height: '120px',
                      backgroundColor: '#f0f0f0',
                      borderRadius: '8px',
                      margin: '0 auto 20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '36px',
                      color: '#666',
                      border: '2px solid #ddd'
                    }}>
                      {team.name.charAt(0)}
                    </div>
                  )}
                  
                  {/* Team Name */}
                  <h3 style={{ 
                    fontSize: '1.1rem', 
                    margin: '0',
                    color: '#333',
                    fontWeight: '500'
                  }}>
                    {team.name}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            No teams found for this group
          </div>
        )}
      </div>
    </div>
  );
};

export default Teams; 